import type { LucideIcon } from 'lucide-react';
import { CircleHelp, BookX, Users, Repeat, Frown } from 'lucide-react';
import type { KGResolutionType } from '@/lib/api/knowledgeGaps';

export const CATEGORY_ICON_MAP: Record<string, { icon: LucideIcon; label: string }> = {
  unanswered_question: { icon: CircleHelp, label: 'Unanswered Questions' },
  missing_knowledge: { icon: BookX, label: 'Missing Knowledge' },
  segment_mismatch: { icon: Users, label: 'Segment Mismatch' },
  repeated_question: { icon: Repeat, label: 'Repeated Questions' },
  user_dissatisfaction: { icon: Frown, label: 'User Dissatisfaction' },
};

export const SORT_OPTIONS = [
  { value: 'occurrence_count_desc', label: 'Most Occurrences First' },
  { value: 'first_seen_desc', label: 'Most Recent First' },
] as const;

export type SortBy = (typeof SORT_OPTIONS)[number]['value'];

export const RESOLUTION_OPTIONS: { value: 'all' | KGResolutionType; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'prompt_update', label: 'Prompt update' },
  { value: 'other', label: 'Knowledge source' },
  { value: 'product_capture', label: 'Product capture' },
  { value: 'config_change', label: 'Config change' },
];

export type StatusFilter = 'all' | 'active' | 'resolved' | 'dismissed';

export const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Open' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'dismissed', label: 'Dismissed' },
];

// Maps the Status filter value to the comma-separated `status` list expected
// by the summaries endpoint. 'active' groups in-progress (both prompt-update
// and product-capture transient states) with active so the "working queue"
// stays visible under a single filter — without including resolving_via_product_capture
// here, gaps would vanish from the list immediately after launching a capture.
export const STATUS_FILTER_TO_PARAM: Record<StatusFilter, string> = {
  all: 'active,resolving_via_prompt,resolving_via_product_capture,resolved,dismissed',
  active: 'active,resolving_via_prompt,resolving_via_product_capture',
  resolved: 'resolved',
  dismissed: 'dismissed',
};

export const PAGE_SIZE = 20;

// Polling cadence for in-flight prompt-update batches. Matches the
// /create-version workflow polling rhythm. Backs off after 2 min to avoid
// hot-looping if a batch is stuck.
export const POLL_INTERVAL_MS = 3000;
export const POLL_BACKOFF_MS = 10000;
export const POLL_BACKOFF_AFTER_MS = 120000;
export const POLL_SLOW_NOTICE_AFTER_MS = 300000;
export const LOW_CONFIDENCE_THRESHOLD = 0.6;
